import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Popover,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PlayerStatusBadge } from 'components/common/hitters/player-status-badge';
import { CommonContentWithSidebar } from 'components/common/layout/content-with-sidebar';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { TableProvider } from 'components/common/table/context';
import { GameDataFooter } from 'components/sections/game-data/footer';
import { ImportSettingsPopover } from 'components/sections/game-data/import-settings-popover';
import { GameDataPitches } from 'components/sections/game-data/pitches';
import { GameDataPitchesSidebar } from 'components/sections/game-data/pitches/sidebar';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { GameDataPitcherRepertoire } from 'components/sections/game-data/players/pitcher-repertoire';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { Breadcrumbs } from 'components/sections/header/breadcrumbs';
import { GameDataContext } from 'contexts/game-data.context';
import { GameDataTab } from 'enums/game-data.enums';
import { t } from 'i18next';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import { MlbStatsService } from 'services/mlb-stats.service';

const COMPONENT_NAME = 'GameDataPlayer';

const getLeague = (player: IMlbPlayer) => {
  switch (player.sportId) {
    case MlbSportId.MLB: {
      return 'MLB';
    }

    case MlbSportId.AAA: {
      return 'AAA';
    }

    default: {
      return '';
    }
  }
};

export const GameDataPlayer = (props: { mode: GameDataTab }) => {
  const { playerPk } = useParams();
  const navigate = useNavigate();
  const { seasonPlayers } = useContext(GameDataContext);

  const player = useMemo(() => {
    if (!playerPk) {
      return undefined;
    }

    const playerPkInt = parseInt(playerPk);
    return seasonPlayers.find((p) => p.playerPk === playerPkInt);
  }, [playerPk, seasonPlayers]);

  const teamName = useMemo(() => {
    if (!player) {
      return t('common.loading');
    }

    const { _mx_team_details } = player;

    if (_mx_team_details?.code && _mx_team_details?.shortName) {
      return `${_mx_team_details.code} ${_mx_team_details.shortName}`;
    }

    if (_mx_team_details?.name) {
      return _mx_team_details.name;
    }

    return 'Inactive';
  }, [player]);

  const [pitches, setPitches] = useState<IMlbPitchExt[]>();
  const [repertoireOpen, setRepertoireOpen] = useState(false);

  useEffect(() => {
    if (pitches) {
      // we've already loaded data
      return;
    }

    if (!player) {
      return;
    }

    if (!props.mode) {
      return;
    }

    MlbStatsService.getInstance()
      .getPlayerPitches({
        mode: props.mode === 'hitters' ? 'hitter' : 'pitcher',
        player: player,
      })
      .then((result) => setPitches(result));
  }, [props.mode, player, pitches]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonContentWithSidebar
        left={
          <FlexTableWrapper
            header={
              <>
                <Breadcrumbs
                  items={[
                    {
                      label: 'common.pitches',
                      onClick: () => navigate(paths.pitches.library),
                    },
                    {
                      label: 'main.game-data',
                      onClick: () => navigate(paths.pitches.gameData.root),
                    },
                    {
                      label: player?.name ?? 'common.loading',
                    },
                  ]}
                />

                <Flex gap="3" align="center">
                  <Skeleton loading={!player}>
                    <GameDataPlayerAvatar
                      size="7"
                      name={player?.name}
                      playerPk={player?.playerPk ?? 0}
                    />
                  </Skeleton>

                  <Flex flexGrow="1" direction="column" gap="2">
                    <Skeleton loading={!player}>
                      {player && (
                        <Flex gap="2" align="center" wrap="wrap">
                          <Box minWidth="0">
                            <Heading size="6" truncate>
                              {player.name}
                            </Heading>
                          </Box>
                          <Flex gap={RADIX.FLEX.GAP.XS}>
                            <Badge color={RADIX.COLOR.NEUTRAL}>
                              {getLeague(player)}
                            </Badge>
                            <Badge color={RADIX.COLOR.NEUTRAL}>
                              {player.season}
                            </Badge>
                            {props.mode === 'pitchers' && (
                              <PitcherHandBadge value={player.pitchHand} />
                            )}
                            {props.mode === 'hitters' && (
                              <HitterSideBadge value={player.batSide} />
                            )}
                            <PlayerStatusBadge value={player.statusCode} />
                          </Flex>
                        </Flex>
                      )}
                    </Skeleton>

                    <Skeleton loading={!player}>
                      <Flex gap="2" align="center">
                        <GameDataTeamLogo
                          size="20px"
                          teamPK={player?._mx_team_details?.teamPk}
                          name={player?._mx_team_details?.name}
                        />

                        <Box minWidth="0">
                          <Text color={RADIX.COLOR.SECONDARY} truncate>
                            {teamName}
                          </Text>
                        </Box>
                      </Flex>
                    </Skeleton>
                  </Flex>

                  <ImportSettingsPopover />

                  {props.mode === 'pitchers' && player && pitches && (
                    <Popover.Root
                      onOpenChange={(open) => setRepertoireOpen(open)}
                    >
                      <Popover.Trigger>
                        <Button>
                          Repertoire{' '}
                          {repertoireOpen ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )}
                        </Button>
                      </Popover.Trigger>
                      <Popover.Content asChild>
                        <Card>
                          <GameDataPitcherRepertoire
                            pitcher={player}
                            pitches={pitches}
                          />
                        </Card>
                      </Popover.Content>
                    </Popover.Root>
                  )}
                </Flex>
              </>
            }
            table={
              <TableProvider identifier="GDPlayerPitchesTableProvider">
                <GameDataPitches
                  mode={props.mode}
                  playerPk={player?.playerPk}
                  pitches={pitches}
                />
              </TableProvider>
            }
            footer={<GameDataFooter />}
          />
        }
        right={<GameDataPitchesSidebar />}
        vFlex
      />
    </ErrorBoundary>
  );
};
